import { CustomFonts } from 'app/components/custom-fonts';
import React, { Fragment, useEffect } from 'react';
import { ContentLoading } from 'app/components/content-loading';
import { PageContainer } from 'app/components/ui/page-container';
import { ErrorModal } from 'app/components/error-modal';
import { useTheme } from 'app/shared/contexts/theme';
import { useBuilding } from 'app/shared/contexts/building';
import { useUser } from 'app/shared/contexts/user';
import { useContent } from 'app/shared/contexts/content';
import { useErrors } from 'app/shared/contexts/errors';
import { ErrorPage } from 'app/pages/error';
import { useApi } from 'app/shared/contexts/api';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { useDelayedFlag } from 'app/shared/hooks/use-delayed-flag';
import { useAnalytics } from 'app/shared/hooks/use-analytics-hook';
import { AppThemeProvider, defaultTheme } from '@hqo/react-components-library';

interface AppContentWrapperProps {
  children: React.ReactNode;
}

// Only show our own loading spinner if content takes longer than this to load. Quicker
// loads (the common case — a few hundred ms) show nothing, since the host webview's own
// spinner already covers that window; our spinner would just be a flash.
const LOADING_SPINNER_DELAY_MS = 700;

export const AppContentWrapper = ({ children }: AppContentWrapperProps): JSX.Element => {
  const { theme, isLoading: isThemeLoading } = useTheme();
  const { building } = useBuilding();
  const { isLoading: isUserLoading } = useUser();
  const { isLoading: isContentLoading } = useContent();
  const { genericErrors } = useErrors();
  const { apiUrl } = useApi();
  const client = useMiniappSdk();

  // Analytics + web-tracking init. Previously this hung off AppRoot, which renders nothing but a
  // <Navigate> off `/` — it unmounted before the building and user queries resolved, so the effects
  // only ever ran with null data. Sentry kept a null user for the whole session and fell back to
  // identifying people by IP. This wrapper is mounted for every route, above the loading gate.
  useAnalytics();

  // Hide the native container header as soon as the SDK client is available. The miniapp
  // renders its OWN header (back/share) in both the loading screen and the content view,
  // so the native chrome is never wanted — showing it lags the SDK bridge and reads as
  // jumpy, and it would double up with the miniapp's own header. (VIS-366)
  useEffect(() => {
    client?.header?.hideHeader();
  }, [client]);

  // Single, aggregate readiness signal (drives the loading gate below): true only once
  // every piece of data the view needs is present.
  const isReady = !!theme && !!building && !isThemeLoading && !isUserLoading && !isContentLoading;

  // Only reveal our spinner if the load actually runs long (see LOADING_SPINNER_DELAY_MS).
  const showLoadingSpinner = useDelayedFlag(!isReady, LOADING_SPINNER_DELAY_MS);

  if (genericErrors.length > 0 || !apiUrl) {
    return <ErrorPage />;
  }

  // Single, aggregate loading gate. Hold until theme, building, user AND content are all
  // present, then swap to content once (one render, no per-stage re-mount/flash). While
  // loading we render a blank loading container and only reveal the spinner once the load
  // has run past LOADING_SPINNER_DELAY_MS — quick loads show nothing (the host webview's
  // own spinner covers them). Note: the content query is disabled (isLoading === false)
  // on non-content routes, so this doesn't block them.
  if (!isReady) {
    return (
      <PageContainer isLoading data-testid="loading-container">
        {showLoadingSpinner ? <ContentLoading /> : null}
      </PageContainer>
    );
  }

  // Create a theme that combines defaultTheme with our custom theme data
  const themeWithFonts = {
    ...defaultTheme,
    ...theme,
    fonts: theme.fonts || ['Arial', 'sans-serif'],
  };
  return (
    <AppThemeProvider theme={themeWithFonts}>
      <Fragment>
        <CustomFonts />
        <div className="h-screen bg-white text-foreground">
          {children}
          <ErrorModal />
        </div>
      </Fragment>
    </AppThemeProvider>
  );
};
